import { pendingCommunicationRoute } from "@/communications/navigation";
import { refreshAuthSession } from "@/lib/auth-client";
import { pendingFormRoute } from "@/forms/navigation";
// src/lib/home-route.ts
//
// Para onde vai quem acabou de entrar (back#176 / front#153): a "Página
// inicial" do Selo prioritário, da marca (Admin › Branding) ou a visão geral.
//
// A guarda do layout aplica as permissões e funcionalidades do destino; a
// página inicial não concede acesso adicional.
import type { QueryClient } from "@tanstack/react-query";
import { badgeHomeDestination } from "./badge-home-link";
import {
  myHomeBadgesQueryKey,
  getMyHomeBadges,
  brandingQueryKey,
  getBranding,
  type BrandingSettings,
} from "@/api";

const OPENING_PAGE_ROUTE = { showcase: "/showcase", feed: "/feed", painel: "/admin" } as const;

export type HomeRoute = "/" | (typeof OPENING_PAGE_ROUTE)[keyof typeof OPENING_PAGE_ROUTE];

// Marca que nunca escolheu fica onde sempre esteve: a visão geral.
export function homeRouteFor(openingPage: BrandingSettings["openingPage"]): HomeRoute {
  return openingPage ? OPENING_PAGE_ROUTE[openingPage] : "/";
}

// Lê os destinos da sessão recém-aberta. Falhas não impedem a entrada.
export async function resolveHomeRoute(qc: QueryClient) {
  const communication = pendingCommunicationRoute();
  if (communication) {
    await refreshAuthSession();
    return communication;
  }
  const form = pendingFormRoute();
  if (form) {
    // O destino direto não passa pelas consultas da home; confirme o estado do
    // SDK antes de montar a guarda da sessão recém-aberta.
    await refreshAuthSession();
    return form;
  }
  const [badges, branding] = await Promise.allSettled([
    qc.fetchQuery({ queryKey: myHomeBadgesQueryKey, queryFn: getMyHomeBadges, staleTime: 0 }),
    qc.fetchQuery({ queryKey: brandingQueryKey, queryFn: getBranding, staleTime: 0 }),
  ]);
  // O backend devolve as concessões na prioridade canônica e anula destinos
  // vencidos, apagados ou despublicados. Não depende da exibição no perfil.
  const badge =
    badges.status === "fulfilled"
      ? badges.value.find((item) => item.valid && item.homeLink)
      : undefined;
  if (badge?.homeLink) return badgeHomeDestination(badge.homeLink);
  return {
    to: homeRouteFor(
      branding.status === "fulfilled" ? branding.value.settings?.openingPage : undefined,
    ),
  };
}
